import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tooltip", "content"]

  showTooltip(event) {
    const element = event.target
    const storeName = element.dataset.storeName
    const productName = element.dataset.productName
    const sku = element.dataset.sku
    const status = element.dataset.status
    const value = element.dataset.value
    const recorded = element.dataset.recorded
    const userName = element.dataset.userName
    const rangingForm = element.dataset.rangingForm
    const grading = element.dataset.grading
    const warehouseGrading = element.dataset.warehouseGrading
    const rangingDate = element.dataset.rangingDate
    const purchaseStatus = element.dataset.purchaseStatus
    const purchaseDate = element.dataset.purchaseDate
    const orderedAt = element.dataset.orderedAt
    const orderForm = element.dataset.orderForm
    const purchaseForm = element.dataset.purchaseForm
    const lapsedAt = element.dataset.lapsedAt

    // Build form history badges
    let formBadges = ''

    if (rangingForm && rangingForm !== 'null' && rangingForm.length > 0) {
      formBadges += `
        <div class="flex items-center gap-1.5">
          <span class="text-gray-400 text-[10px] w-14 text-right shrink-0">Field</span>
          <div class="flex items-center gap-1">
            ${this.renderFormSquares(rangingForm.substring(0, 5), 'ranging')}
          </div>
        </div>
      `
    }

    if (orderForm && orderForm !== 'null' && orderForm.length > 0) {
      formBadges += `
        <div class="flex items-center gap-1.5">
          <span class="text-gray-400 text-[10px] w-14 text-right shrink-0">Orders</span>
          <div class="flex items-center gap-1">
            ${this.renderFormSquares(orderForm.substring(0, 5), 'order')}
          </div>
        </div>
      `
    }

    if (purchaseForm && purchaseForm !== 'null' && purchaseForm.length > 0) {
      formBadges += `
        <div class="flex items-center gap-1.5">
          <span class="text-gray-400 text-[10px] w-14 text-right shrink-0">Purchases</span>
          <div class="flex items-center gap-1">
            ${this.renderFormSquares(purchaseForm.substring(0, 5), 'purchase')}
          </div>
        </div>
      `
    }

    let winFormBadge = ''
    if (formBadges) {
      winFormBadge = `
        <div class="mt-3 pt-3 border-t border-gray-700">
          <div class="bg-blue-800 border border-blue-700 rounded-lg p-2 shadow-md" style="width: 192px;">
            <div class="text-white font-bold tracking-wider text-xs mb-1.5 text-center" style="font-family: 'Raleway', sans-serif;">WIN FORM</div>
            <div class="bg-white rounded p-1.5 space-y-1">
              ${formBadges}
            </div>
          </div>
        </div>
      `
    }

    // Build tooltip content - some fields are optional (e.g., Ranging Report omits value/recorded/by)
    let extraFields = ''
    if (value && value !== 'undefined') {
      extraFields += `<div>Value: ${value}</div>`
    }
    if (recorded && recorded !== 'undefined') {
      extraFields += `<div>Recorded: ${recorded}</div>`
    }
    if (userName && userName !== 'undefined') {
      extraFields += `<div class="text-sm text-blue-300 mt-1">By: ${userName}</div>`
    }

    let gradingFields = ''
    if (grading && grading !== 'undefined' && grading !== '') {
      gradingFields += `<div>Store Grading: ${grading}</div>`
    }
    if (warehouseGrading && warehouseGrading !== 'undefined' && warehouseGrading !== '') {
      gradingFields += `<div>Warehouse Grading: ${warehouseGrading}</div>`
    }

    let dateFields = ''
    if (rangingDate && rangingDate !== '') {
      dateFields += `<div class="text-gray-300">Ranging: ${rangingDate}</div>`
    }
    if (purchaseStatus && purchaseStatus !== '' && purchaseStatus !== 'Unknown') {
      let purchaseLine = `Purchase: ${purchaseStatus}`
      if (purchaseDate && purchaseDate !== '') {
        purchaseLine += ` (${purchaseDate})`
      }
      dateFields += `<div class="text-gray-300">${purchaseLine}</div>`
    }
    if (orderedAt && orderedAt !== '') {
      dateFields += `<div class="text-gray-300">Order: Ordered ${orderedAt}</div>`
    }

    let statusLine = `<div>Status: ${status}</div>`
    if (lapsedAt && lapsedAt !== '') {
      statusLine = `<div>Not Ranged (Previously on shelf)</div><div class="text-gray-300">Last stocked: ${lapsedAt}</div>`
    }

    this.contentTarget.innerHTML = `
      <div class="font-medium">${storeName}</div>
      <div class="text-sm text-gray-300">${sku} • ${productName}</div>
      ${statusLine}
      ${gradingFields}
      ${dateFields}
      ${extraFields}
      ${winFormBadge}
    `

    this.tooltipTarget.style.left = (event.pageX + 10) + 'px'
    this.tooltipTarget.style.top = (event.pageY - 10) + 'px'
    this.tooltipTarget.classList.remove('opacity-0')
  }

  renderFormSquares(form, formType) {
    const colorMaps = {
      ranging: {
        'I': { bg: 'bg-green-500', tooltip: 'In Stock' },
        'O': { bg: 'bg-orange-500', tooltip: 'Out of Stock' },
        'C': { bg: 'bg-blue-500', tooltip: 'Cut In' },
        'X': { bg: 'bg-gray-400', tooltip: 'Not Ranged' }
      },
      order: {
        'T': { bg: 'bg-pink-500', tooltip: 'Ordered (TIO)' },
        'X': { bg: 'bg-gray-400', tooltip: 'Not Ordered' }
      },
      purchase: {
        'P': { bg: 'bg-green-500', tooltip: 'Purchased' },
        'X': { bg: 'bg-gray-400', tooltip: 'Not Purchased' }
      }
    }
    const colors = colorMaps[formType] || colorMaps.ranging

    return form.split('').map(char => {
      const colorData = colors[char] || { bg: 'bg-gray-300', tooltip: 'Unknown' }
      return `<div class="w-4 h-4 ${colorData.bg} rounded-sm shadow-sm cursor-help hover:scale-110 transition-transform" title="${colorData.tooltip}"></div>`
    }).join('')
  }

  hideTooltip() {
    this.tooltipTarget.classList.add('opacity-0')
  }
}
